import CancelIcon from "@mui/icons-material/Cancel";
import StarIcon from "@mui/icons-material/Star";
import { grey, red } from "@mui/material/colors";
import Loader from "components/Loader/Loader";
import { useAppDispatch } from "hooks/hooks";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { FormatDateIso } from "utils/dateUtils";
import { resourceCategoryDictionary } from "utils/translationDictionary";
import {
  getResourcesList,
  selectState,
  setDeleteResourceModalOpen,
  setSelectedIndex,
  setSort,
} from "../main.slice";
import { selectState as selectStateAuth } from "authentication/main.slice";
import DeleteResourceModal from "./DeleteResourceModal";
import DatePicker from "react-datepicker";
import { IUnit } from "Dashboard/domain/IUnit";
import AverageConsumptionSelect from "Dashboard/scenes/Resources/components/AverageConsumptionSelect";
import { debounce } from "lodash";
import { updateResourceDetails } from "../../Details/details.slice";
import { IUpdateResource } from "Dashboard/scenes/Resources/domain/IUpdateResource";
import { toast } from "react-toastify";
import PartnerSimpleSelect from "Dashboard/scenes/Partners/components/PartnerSimpleSelect";
import { styles } from "Dashboard/components/SelectStyle";
import { usePartnerOptions } from "Dashboard/scenes/Partners/hooks/PartnerOptions";
import { hasFullAccessTo } from "utils/authUtil";
import { RESOURCES_MODULE_NAME } from "Dashboard/scenes/Resources/domain/ResourcesModule";
import { UseFormGetValues, UseFormSetValue } from "react-hook-form";
import { IGetResourcesListRequestParams } from "Dashboard/scenes/Resources/domain/IGetResourcesListRequestParams";
import { useWindowedRows } from "hooks/useWindowedRows";

interface IProps {
  getValuesSearch: UseFormGetValues<IGetResourcesListRequestParams>;
  setValueSearch: UseFormSetValue<IGetResourcesListRequestParams>;
};

// Keep in sync with `.g-grid.g-content .row` height ($grid-content-row-height).
const ROW_HEIGHT = 20;

const TopSection = (props: IProps) => {
  const { loading, resources, selectedIndex } =
    useSelector(selectState);
  const { user } = useSelector(selectStateAuth);
  const { getValuesSearch, setValueSearch } = props;
  const dispatch = useAppDispatch();
  const partnerOptions = usePartnerOptions();

  useEffect(() => {
    dispatch(getResourcesList(getValuesSearch()));
  }, [dispatch, getValuesSearch]);

  const showRefuelsAtDate = getValuesSearch("showRefuelsAtDate");
  const [selectedDate, setSelectedDate]
    = useState<Date | null>(showRefuelsAtDate
      ? new Date(showRefuelsAtDate) : null);

  const handleDateChange = (date: Date | null) => {
    let formattedDate = undefined;
    if (date) {
      formattedDate = FormatDateIso(date);
    }
    let newFilter = {
      ...getValuesSearch(),
      showRefuelsAtDate: formattedDate
    }
    setSelectedDate(date);
    setValueSearch('showRefuelsAtDate', formattedDate, {
      shouldValidate: false,
      shouldDirty: true,
    })
    dispatch(getResourcesList(newFilter));
  };

  const unitFullName = (unit: IUnit) => {
    if (unit.name.toLowerCase() === 'l') {
      return "litara";
    }
    return unit.name;
  };

  const onChangeBasic = debounce(async (data: IUpdateResource) => {
    try {
      await dispatch(updateResourceDetails(data)).unwrap();
      toast.success("Resurs je uspešno izmenjen.");
    } catch (status: any) {
      if (status === 404 || status === 403 || status === 400) {
        toast.error("Izmena nije moguća zbog neočekivane greške");
      }
    }
  }, 1000);

  const onChangePartner = async (resourceId: number, newPartnerId: number) => {
    onChangeBasic({ resourceId: resourceId, supplierPartnerId: newPartnerId });
  };

  const handleSort = (sortKey: string, typeOfValue: 'string' | 'boolean' | 'date') => {
    dispatch(setSort({ key: sortKey, typeOfValue: typeOfValue }));
  };

  const { contentRef, startIndex, endIndex, topSpacer, bottomSpacer } =
    useWindowedRows({
      count: resources.length,
      rowHeight: ROW_HEIGHT,
      overscanPx: 1000,
    });

  return (
    <section className="loading-container">
      {loading && <Loader />}

      {!loading && (
        <>
          <div className="g-grid g-header">
            <div className="row">
              <div className="col lowercase center header-cell">
                <span>ID broj</span>
              </div>
              <div className="col lowercase center header-cell">
                <span>Registracija</span>
              </div>
              <div className="col lowercase header-cell clickable"
                onClick={() => handleSort("name", "string")}>
                <span className="margin-cell">Brend, marka, tip</span>
              </div>
              <div className="col lowercase center header-cell clickable"
                onClick={() => handleSort("category.name", "string")}>
                <span>kategorija</span>
              </div>
              <div className="col lowercase">
                <span>dobavljač</span>
              </div>
              <div className="col lowercase">
                <span></span>
              </div>
              <div className="col lowercase header-cell clickable"
                onClick={() => handleSort("dateCharge", "date")}>
                <span>Trenutna zaduženja po projektu</span>
              </div>
              <div className="col lowercase date-cell">
                <DatePicker
                  placeholderText="Izaberi datum"
                  showMonthDropdown
                  showYearDropdown
                  dateFormat="dd.MM.yyyy"
                  onChange={handleDateChange} // Call handleDateChange when the date changes
                  selected={selectedDate} // Set the selected date
                  isClearable={true}
                />
                <span>Datuma sipao gorivo</span>
              </div>
              <div className="col lowercase">Prosečna potrošnja</div>
            </div>
          </div>
          <div className="g-grid g-content" ref={contentRef}>
            {topSpacer > 0 && <div style={{ height: topSpacer }} aria-hidden />}
            {resources.slice(startIndex, endIndex).map((resourceListItem, offset) => {
              const index = startIndex + offset;
              return (
                <div
                  className={`row ${selectedIndex === index ? "active" : ""}`}
                  key={index}
                  onClick={() => {
                    dispatch(setSelectedIndex(index));
                  }}
                >
                  <div className="col center">
                    <input className="bg-item center grey"
                      type="text"
                      defaultValue={resourceListItem.idNumber}
                      onChange={(e) => {
                        onChangeBasic({
                          resourceId: resourceListItem.resourceId,
                          idNumber: e.target.value
                        })
                      }} />
                  </div>
                  <div className="col center">
                    <input className="bg-item center grey"
                      type="text"
                      defaultValue={resourceListItem.registrationPlate}
                      onChange={(e) => {
                        onChangeBasic({
                          resourceId: resourceListItem.resourceId,
                          registrationPlate: e.target.value
                        })
                      }} />
                  </div>
                  <div className="col uppercase strong">
                    <div className={`bg-item${resourceListItem.status === "INACTIVE" ? " light" : ""}`}>
                      {resourceListItem.name}
                      {resourceListItem.hasAlarm && (
                        <div className="badge orange white">
                          <StarIcon
                            style={{
                              fontSize: 10,
                              color: red[300],
                            }}
                            className="starIcon"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col uppercase text-small">
                    <span className="secondary-text">
                      {false && hasFullAccessTo(user, RESOURCES_MODULE_NAME)
                        && (
                          <CancelIcon
                            style={{
                              cursor: "pointer",
                              fontSize: 15,
                              color: grey[300],
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              dispatch(
                                setDeleteResourceModalOpen({ visible: true, index })
                              );
                            }}
                          />
                        )}
                      {
                        resourceCategoryDictionary[
                        resourceListItem.category.name
                        ]
                      }
                    </span>
                  </div>
                  <div className="col">
                    <div className="normal">
                      <PartnerSimpleSelect
                        customStyles={styles.dropdownInTable}
                        defaultValue={resourceListItem?.supplier?.partnerId}
                        options={partnerOptions}
                        onChange={(partnerId: number) => onChangePartner(resourceListItem.resourceId, partnerId)} />
                    </div>
                  </div>
                  <div className="col uppercase bold">
                    <span>{">"}</span>
                  </div>
                  <div className="col text-only">
                    {resourceListItem.currentCharges.length > 0 && (
                      <span className="strong">
                        {`${resourceListItem.currentCharges[0].project.name} `}
                        <span className="text-small normal">
                          : {resourceListItem.currentCharges[0].employee.name},{" "}
                          {resourceListItem.currentCharges[0].dateCharge}.
                        </span>
                      </span>
                    )}
                  </div>
                  <div className="col">
                    {resourceListItem.refuelOnDate && (
                      <>
                        <span className="strong">
                          {'>' + resourceListItem.refuelOnDate.quantityAmount + ' '}
                          <span className="normal text-small">
                            {unitFullName(resourceListItem.refuelOnDate.quantityUnit)}
                          </span>
                        </span>
                      </>
                    )}
                  </div>
                  <div className="col">
                    {'>'} {resourceListItem.averageConsumptions.length > 0 &&
                      <AverageConsumptionSelect resourceListItem={resourceListItem} />}
                  </div>
                  <div></div>
                </div>
              );
            })}
            {bottomSpacer > 0 && <div style={{ height: bottomSpacer }} aria-hidden />}
          </div>
          <div className="g-grid g-footer">
            <div className="row">
              <div className="col strong">
                <span>
                  {resources.length}&nbsp;ukupno
                </span>
              </div>
            </div>
          </div>
          <DeleteResourceModal />
        </>
      )}
    </section>
  );
};

export default TopSection;
